import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthAPI, InvitationAPI } from "../utils/firebase";

const AuthCtx = createContext(null);

// Allow /app/dashboard or /ppapp/dashboard, configurable at build time
const POST_LOGIN_PATH = process.env.REACT_APP_POST_LOGIN_PATH || "/app/dashboard";

const INVITE_KEY = "pp:invite";
const INVITE_ERR_FLAG = "pp:inviteError"; // used to show a banner after redirect

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [initializing, setInitializing] = useState(true);
  const [ready, setReady] = useState(undefined);
  const [reason, setReason] = useState("");

  // 'idle' | 'checking' | 'ok' | 'blocked'
  const [betaStatus, setBetaStatus] = useState("idle");

  // Bootstrap Firebase + auth state listener + redirect result
  useEffect(() => {
    const ok = AuthAPI.isFirebaseReady();
    setReady(ok);
    setReason(ok ? "" : AuthAPI.notReadyReason());

    if (!ok) {
      setInitializing(false);
      return;
    }

    let first = true;
    const unsub = AuthAPI.onAuthChangedSafe((u) => {
      setUser(u || null);
      setBetaStatus("idle");
      if (first) {
        setInitializing(false);
        first = false;
      }
    });

    (async () => {
      try {
        await AuthAPI.handleAuthRedirect();
      } catch {}
    })();

    return () => {
      try { unsub && unsub(); } catch {}
    };
  }, []);

  // App profile (admin flag, etc.) listener
  useEffect(() => {
    if (!user) {
      setAppUser(null);
      setIsAdmin(false);
      return;
    }
    let unsub = () => {};
    try {
      unsub = AuthAPI.subscribeAppUserProfile({
        user,
        onData: (doc) => {
          setAppUser(doc);
          setIsAdmin(!!doc?.admin);
        },
        onError: () => {
          setAppUser(null);
          setIsAdmin(false);
        },
      });
    } catch {
      setAppUser(null);
      setIsAdmin(false);
    }
    return () => { try { unsub && unsub(); } catch {} };
  }, [user]);

  const loginWithGoogle = async () => {
    sessionStorage.setItem("pp:postLogin", POST_LOGIN_PATH);
    await AuthAPI.fcLoginWithGoogle(); // alias to loginWithGoogle
  };

  const logout = async () => {
    await AuthAPI.logoutUser();
    try {
      sessionStorage.removeItem("pp:postLogin");
      localStorage.removeItem("pp:postLogin");
    } catch {}
    // Hard redirect to root to avoid any 404 flash from SPA routing
    try {
      window.location.replace("/");
    } catch {
      window.location.href = "/";
    }
  };

  // Auto-bounce if unauthenticated while on any /app/* route (token expiry, manual URL, etc.)
  useEffect(() => {
    if (!initializing && ready && !user) {
      if (location.pathname.startsWith("/app")) {
        navigate("/", { replace: true });
      }
    }
  }, [user, initializing, ready, location.pathname, navigate]);

  // Beta invitation verification — sessionStorage-only + loop-proof
  useEffect(() => {
    (async () => {
      if (!user) return;
      if (betaStatus === "ok") return;

      // Only honor invite set in THIS session; ignore localStorage to avoid stale blocks.
      const invite = sessionStorage.getItem(INVITE_KEY) || "";

      if (!invite) {
        setBetaStatus("ok");
        return;
      }

      setBetaStatus("checking");

      // Clear key BEFORE verifying to avoid loops on errors
      try { sessionStorage.removeItem(INVITE_KEY); } catch {}

      const ok = await InvitationAPI.verifyBetaInvitationForEmail(user.email, invite);
      if (ok) {
        setBetaStatus("ok");
        return;
      }

      // Blocked: mark flag for marketing banner and log user out
      setBetaStatus("blocked");
      try {
        sessionStorage.removeItem("pp:postLogin");
        localStorage.removeItem("pp:postLogin");
        sessionStorage.setItem(INVITE_ERR_FLAG, "denied");
      } catch {}

      try { await AuthAPI.logoutUser(); } catch {}

      // Hard redirect out of app so protected routes never render
      try { window.location.replace("/"); } catch { window.location.href = "/"; }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      appUser,
      isAdmin,
      initializing,
      loginWithGoogle,
      logout,
      isFirebaseReady: ready,
      firebaseError: reason,
      betaStatus,
    }),
    [user, appUser, isAdmin, initializing, ready, reason, betaStatus]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
